// eventListeners.web.js

/**
 * Register footnote and front-arrow click handlers (delegation).
 * Returns a cleanup function.
 */
export const registerEvents = (container, interfaceExecuteCall) => {
    window.executeCallbackFunction = interfaceExecuteCall;

    const footnoteHandler = e => {
        const target = e.target.closest('.footnote');
        if (!target) return;
        const id = target.getAttribute('id');
        if (id) interfaceExecuteCall('activateFootnote', id);
    };
    container.addEventListener('click', footnoteHandler);

    const arrowHandler = e => {
        const target = e.target.closest('.front-arrow');
        if (!target) return;
        interfaceExecuteCall('nextChapter');
    };
    container.addEventListener('click', arrowHandler);

    return () => {
        container.removeEventListener('click', footnoteHandler);
        container.removeEventListener('click', arrowHandler);
    };
};

// ─── Selection Helpers ───────────────────────────────────────────────

const isSelectionBackwards = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return false;
    const range = document.createRange();
    try {
        range.setStart(sel.anchorNode, sel.anchorOffset);
        range.setEnd(sel.focusNode, sel.focusOffset);
        const backwards = range.collapsed;
        range.detach();
        return backwards;
    } catch {
        return false;
    }
};

const cutVerseNumber = idString => {
    const verseNumberString = idString.substring(1, idString.length);
    try {
        const number = parseInt(verseNumberString) - 1;
        return 'V' + number;
    } catch (e) {
        console.log(e);
    }
    return null;
};

/**
 * Resolve which verse range the current selection spans.
 */
const resolveVerseRange = selection => {
    const isBackwards = isSelectionBackwards();
    if (!selection || !selection.rangeCount) return null;

    let anchor, endAnchor;
    if (isBackwards) {
        anchor = selection.focusNode;
        endAnchor = selection.anchorNode;
    } else {
        anchor = selection.anchorNode;
        endAnchor = selection.focusNode;
    }

    const verseElements = document.getElementsByClassName('verse');
    let lastVerse = null;
    let firstVerse = null;
    let setLast = false;

    for (const verseEl of verseElements) {
        if (verseEl.compareDocumentPosition(anchor) & Node.DOCUMENT_POSITION_PRECEDING) {
            if (!lastVerse) {
                lastVerse = verseEl.id;
            }
        } else if (verseEl.compareDocumentPosition(endAnchor) & Node.DOCUMENT_POSITION_FOLLOWING) {
            firstVerse = verseEl.id;
        }

        if (!setLast && verseEl.compareDocumentPosition(endAnchor) & Node.DOCUMENT_POSITION_PRECEDING) {
            const endVerseId = cutVerseNumber(verseEl.id);
            lastVerse = endVerseId;
            setLast = true;
        }

        if (verseEl.compareDocumentPosition(endAnchor) & Node.DOCUMENT_POSITION_PRECEDING) {
            const endVerseId = cutVerseNumber(verseEl.id);
            if (endVerseId === firstVerse) {
                lastVerse = firstVerse;
                break;
            }
        }
    }

    if (!firstVerse) firstVerse = 'V1';
    if (!lastVerse) lastVerse = firstVerse;
    if (lastVerse === 'V0') lastVerse = firstVerse;

    return firstVerse === lastVerse ? firstVerse : firstVerse + ' - ' + lastVerse;
};

// ─── DOM-based Selection Toolbar ─────────────────────────────────────
// Managed entirely outside React to avoid re-renders that clear selection.

let toolbarElement = null;
let currentSelectionData = null;

const getToolbarElement = () => {
    if (toolbarElement) return toolbarElement;

    toolbarElement = document.createElement('div');
    toolbarElement.id = 'selection-toolbar';
    toolbarElement.style.cssText = `
        position: fixed;
        top: 56px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 9999;
        display: none;
        flex-direction: row;
        align-items: center;
        gap: 2px;
        padding: 4px 8px;
        border-radius: 12px;
        user-select: none;
        animation: selToolbarIn 0.2s ease;
    `;

    // Prevent toolbar clicks from clearing text selection
    toolbarElement.addEventListener('mousedown', e => {
        e.preventDefault();
        e.stopPropagation();
    });

    // Add animation keyframes
    const style = document.createElement('style');
    style.textContent = `
        @keyframes selToolbarIn {
            from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        #selection-toolbar button {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 6px;
            padding: 8px 16px;
            border: none;
            background: transparent;
            border-radius: 8px;
            cursor: pointer;
            transition: background-color 0.12s ease;
            outline: none;
            -webkit-tap-highlight-color: transparent;
            font-size: 14px;
            white-space: nowrap;
        }
        #selection-toolbar button .icon {
            font-family: 'bibleFont';
            font-size: 18px;
            font-weight: normal;
            font-style: normal;
        }
        #selection-toolbar .divider {
            width: 1px;
            height: 24px;
            flex-shrink: 0;
        }
    `;
    document.head.appendChild(style);

    toolbarElement.innerHTML = `
        <button data-action="save"><span class="icon">&#xe903;</span>Spremi</button>
        <div class="divider"></div>
        <button data-action="note"><span class="icon">&#xe060;</span>Bilješka</button>
        <div class="divider"></div>
        <button data-action="copy"><span class="icon">&#xe040;</span>Kopiraj</button>
    `;

    document.body.appendChild(toolbarElement);
    return toolbarElement;
};

const showToolbar = (theme) => {
    const el = getToolbarElement();
    const colors = theme.colors;

    el.style.display = 'flex';
    el.style.backgroundColor = colors.navBackground;
    el.style.boxShadow = `0 4px 24px ${colors.shadow}`;
    el.style.border = `1px solid ${colors.border}`;
    el.style.animation = 'none';
    // Force reflow for animation restart
    void el.offsetWidth;
    el.style.animation = 'selToolbarIn 0.2s ease';

    // Style buttons and dividers
    el.querySelectorAll('button').forEach(btn => {
        btn.style.color = colors.text;
        btn.style.fontFamily = theme.fontFamilyBold || 'sans-serif';
        btn.onmouseenter = () => { btn.style.backgroundColor = colors.border; };
        btn.onmouseleave = () => { btn.style.backgroundColor = 'transparent'; };
    });
    el.querySelectorAll('.divider').forEach(div => {
        div.style.backgroundColor = colors.border;
    });
};

const hideToolbar = () => {
    if (toolbarElement) {
        toolbarElement.style.display = 'none';
    }
    currentSelectionData = null;
};

/**
 * Register selection toolbar using pure DOM.
 * No React state updates → no re-renders → selection highlight preserved.
 *
 * @param {HTMLElement} container — the reading container
 * @param {object} theme — the current theme object
 * @param {Function} onAction — called with (action, { text, verseRange }) where action is 'save'|'note'|'copy'
 * @param {object} [options] — optional config
 * @param {boolean} [options.suppressContextMenu=true] — suppress native context menu on selection
 * @returns {Function} cleanup
 */
export const registerSelectionToolbar = (container, theme, onAction, options = {}) => {
    const { suppressContextMenu = true } = options;
    let pendingCheck = null;
    let isPointerDown = false;

    const evaluateSelection = () => {
        const sel = document.getSelection();

        if (!sel || sel.isCollapsed || !sel.toString().trim()) {
            hideToolbar();
            return;
        }

        try {
            const range = sel.getRangeAt(0);
            if (!container.contains(range.commonAncestorContainer)) {
                return;
            }

            const verseRange = resolveVerseRange(sel);
            const text = sel.toString();

            currentSelectionData = { text, verseRange };
            showToolbar(theme);
        } catch {
            // ignore
        }
    };

    const onPointerDown = () => {
        isPointerDown = true;
        clearTimeout(pendingCheck);
    };

    const onPointerUp = () => {
        isPointerDown = false;
        clearTimeout(pendingCheck);
        pendingCheck = setTimeout(evaluateSelection, 200);
    };

    // selectionchange is needed for mobile long-press selection.
    // Ignore it while pointer is down (desktop drag) to avoid firing mid-selection.
    const onSelectionChange = () => {
        if (isPointerDown) return;
        clearTimeout(pendingCheck);
        pendingCheck = setTimeout(evaluateSelection, 300);
    };

    // Suppress native context menu on mobile when text is selected in our container
    const onContextMenu = e => {
        const sel = document.getSelection();
        if (sel && !sel.isCollapsed && sel.toString().trim()) {
            try {
                const range = sel.getRangeAt(0);
                if (container.contains(range.commonAncestorContainer)) {
                    e.preventDefault();
                }
            } catch {
                // ignore
            }
        }
    };

    // Handle toolbar button clicks
    const onToolbarClick = e => {
        const btn = e.target.closest('button[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;
        if (currentSelectionData) {
            const data = { ...currentSelectionData };
            window.getSelection()?.removeAllRanges();
            hideToolbar();
            onAction(action, data);
        }
    };

    const toolbar = getToolbarElement();
    toolbar.addEventListener('click', onToolbarClick);

    // Only mousedown/mouseup for pointer state (gates selectionchange on desktop drag).
    // touchstart is NOT tracked — mobile long-press needs selectionchange to fire freely.
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('mouseup', onPointerUp);
    document.addEventListener('touchend', onPointerUp);
    document.addEventListener('selectionchange', onSelectionChange);
    if (suppressContextMenu) {
        container.addEventListener('contextmenu', onContextMenu);
    }

    return () => {
        clearTimeout(pendingCheck);
        hideToolbar();
        toolbar.removeEventListener('click', onToolbarClick);
        document.removeEventListener('mousedown', onPointerDown);
        document.removeEventListener('mouseup', onPointerUp);
        document.removeEventListener('touchend', onPointerUp);
        document.removeEventListener('selectionchange', onSelectionChange);
        if (suppressContextMenu) {
            container.removeEventListener('contextmenu', onContextMenu);
        }
    };
};

export const debounce = (fn, delay) => {
    let timer = null;
    return function () {
        var context = this,
            args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function () {
            fn.apply(context, args);
        }, delay);
    };
};
